import React, { useState } from 'react';

import useModal from 'lib/hooks/useModal';

import NewServiceModal from 'components/practice/Settings/components/Services/components/NewServiceModal/NewServiceModal';
import Button from 'components/shared/Button/Button';
import Modal from 'components/shared/Modal/Modal';
import Typography from 'components/shared/Typography/Typography';

import { addServicesModalColumns } from '../../lib/addServicesModalHelpers';
import EditServiceModal from '../EditServiceModal';

import PriceAdjustments from './components/PriceAdjustments';
import ServicesTable from './components/ServicesTable';
import { useAddServicesModal } from './hooks/useAddServicesModal';

const AddServicesModal = ({ isOpen, onClose, onServicesAdded = () => {} }) => {
  const [columnDefs, setColumnDefs] = useState(addServicesModalColumns);

  const {
    isOpen: isCreateServiceOpen,
    openModal: openCreateService,
    closeModal: closeCreateService
  } = useModal();

  const {
    selectedRows,
    standardChargeMultiplier,
    selfPayMultiplier,
    selectedSpecialty,
    gridApi,
    page,
    limit,
    filters,
    tabsData,
    paginatedAndFilteredData,
    totalItems,
    showControls,
    isLoading,
    isFetching,
    setSelectedSpecialty,
    setGridApi,
    setPage,
    setLimit,
    setLocalEdits,
    changeFilters,
    handleStandardChargeChange,
    handleSelfPayChange,
    handleAddServices,
    handleCreateNewService,
    onSelectionChanged,
    refetchCptCodes
  } = useAddServicesModal(onServicesAdded, onClose);

  const handleColumnChange = (updatedColumns) => {
    setColumnDefs((prev) =>
      typeof updatedColumns === 'function' ? updatedColumns(prev) : updatedColumns
    );
  };

  const handleColumnDragEnd = (updatedColumns) => {
    setColumnDefs((prev) =>
      typeof updatedColumns === 'function' ? updatedColumns(prev) : updatedColumns
    );
  };

  const handleCreateNewServiceWrapper = async (newService) => {
    await handleCreateNewService(newService);
    closeCreateService();
    refetchCptCodes();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        handleClose={onClose}
        title="Add services"
        width="medium"
        headButton={
          <div className="mr-1 flex justify-end">
            <Button
              text="Create new service"
              variant="link"
              color="neutral"
              iconSize={16}
              size="xs"
              shade={700}
              className="text-xs"
              icon="add-square-mono"
              iconColor="neutral"
              onClick={openCreateService}
            />
          </div>
        }
        footerActions={{
          primary: {
            onClick: handleAddServices,
            text: selectedRows?.length
              ? `Add ${selectedRows.length} service${selectedRows.length !== 1 ? 's' : ''}`
              : 'Add services',
            isLoading,
            size: 'small',
            isDisabled: selectedRows.length === 0
          }
        }}>
        <div className="flex h-full flex-col justify-between">
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="mb-4 flex flex-shrink-0 flex-col gap-1">
              <Typography weight="semibold" className="text-neutral-900">
                Choose services to add
              </Typography>
              <Typography className="text-sm text-neutral-600">
                Browse all services or use predefined categories to quickly add the ones relevant to
                your practice.
              </Typography>
            </div>

            <ServicesTable
              columnDefs={columnDefs}
              tabsData={tabsData}
              selectedSpecialty={selectedSpecialty}
              onSpecialtyChange={setSelectedSpecialty}
              filters={filters}
              onFilterChange={changeFilters}
              showControls={showControls}
              onColumnChange={handleColumnChange}
              onColumnDragEnd={handleColumnDragEnd}
              paginatedAndFilteredData={paginatedAndFilteredData}
              gridApi={gridApi}
              onGridReady={({ api }) => setGridApi(api)}
              onSelectionChanged={onSelectionChanged}
              page={page}
              limit={limit}
              totalItems={totalItems}
              onPageChange={setPage}
              onLimitChange={setLimit}
              setLocalEdits={setLocalEdits}
              ModalComponent={EditServiceModal}
              isLoading={isFetching}
            />
          </div>

          <div className="my-6 h-px w-full bg-neutral-200" />

          <PriceAdjustments
            standardChargeMultiplier={standardChargeMultiplier}
            selfPayMultiplier={selfPayMultiplier}
            onStandardChargeChange={handleStandardChargeChange}
            onSelfPayChange={handleSelfPayChange}
          />
        </div>
      </Modal>

      {isCreateServiceOpen && (
        <NewServiceModal
          service={{}}
          isOpen={isCreateServiceOpen}
          onClose={closeCreateService}
          onSave={handleCreateNewServiceWrapper}
          isLoading={isLoading}
          parentTitle="Add services"
        />
      )}
    </>
  );
};

export default AddServicesModal;
